import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Alert } from '@instructure/ui-alerts';
import { Button } from '@instructure/ui-buttons';
import { Flex } from '@instructure/ui-flex';
import { Heading } from '@instructure/ui-heading';
import { Text } from '@instructure/ui-text';
import formatMessage from 'format-message';
import { useState } from 'react';
import CanvasSelect from '../shared/CanvasSelect';
export const AutoCaptioning = ({ onCancel, onPrimary, liveRegion, languages, mountNode, }) => {
    const [selectedLanguageId, setSelectedLanguageId] = useState('');
    const [showLanguageError, setShowLanguageError] = useState(false);
    const handleLanguageChange = (_event, languageId) => {
        if (languageId) {
            setSelectedLanguageId(languageId);
            setShowLanguageError(false);
        }
    };
    const handlePrimaryClick = () => {
        if (!selectedLanguageId) {
            setShowLanguageError(true);
            return;
        }
        onPrimary(selectedLanguageId);
    };
    return (_jsxs(Flex, { as: "div", direction: "column", gap: "medium", children: [_jsxs(Flex.Item, { overflowY: "hidden", overflowX: "hidden", children: [_jsx(Heading, { variant: "titleCardMini", children: formatMessage('Automatic captioning') }), _jsx(Text, { variant: "contentSmall", children: formatMessage('Our technology generates ~85% accurate captions.') })] }), _jsxs(Flex, { gap: "small", direction: "column", children: [_jsx(Heading, { variant: "titleCardMini", children: formatMessage('Language Spoken in This Media*') }), _jsx(CanvasSelect, { label: _jsx(ScreenReaderContent, { children: formatMessage('Select Language') }), placeholder: formatMessage('Select Language'), value: selectedLanguageId, mountNode: mountNode, translatedStrings: {
                            USE_ARROWS: formatMessage('Use arrow keys to navigate options.'),
                            LIST_COLLAPSED: formatMessage('List collapsed.'),
                            LIST_EXPANDED: formatMessage('List expanded.'),
                            OPTION_SELECTED: '{option} selected.',
                        }, onChange: handleLanguageChange, messages: showLanguageError
                            ? [
                                {
                                    type: 'newError',
                                    text: formatMessage('Please select a language'),
                                },
                            ]
                            : [], children: languages.map(option => (
                        // @ts-expect-error - CanvasSelect.Option is a JS component without TS definitions
                        _jsx(CanvasSelect.Option, { id: option.id, value: option.id, children: option.label }, option.id))) }), showLanguageError && (_jsx(Alert, { variant: "error", screenReaderOnly: true, isLiveRegionAtomic: true, liveRegion: liveRegion, children: formatMessage('Please select a language') }))] }), _jsxs(Flex, { gap: "small", children: [_jsx(Button, { color: "secondary", onClick: onCancel, textAlign: "center", width: "auto", children: formatMessage('Cancel') }), _jsx(Button, { color: "primary", onClick: handlePrimaryClick, textAlign: "center", width: "auto", children: formatMessage('Request') })] })] }));
};
